import React from 'react';
import useContractBreakdown from '../hooks/useContractBreakdown';
import { money, findingOf, sovSubOf, reasonOf, docLabelOf, summitSovNoteOf, toneColors } from './ContractBreakdownSummary';
import { sovStripStyle, SovStripCell, SovStripDivider, SovStripOp } from './sov/SovLayout';

/* ──────────────────────────────────────────────────────────────────
 * ContractBreakdownCard — the SOV tab's contract check (Duncan 2026-11-03, "show the breakdown"):
 *
 *   Contract on file = SOV on the pay apps + freeform invoices + not billed yet
 *
 * E3's contract is the AIA SOV plus scope billed on freeform invoices (the engineering fee, some change orders), so a
 * contract that's more than the SOV is usually right -- this shows what makes up the difference, and flags only a gap
 * nothing explains. One slim strip in the Summary band (it was a tall card -- "they sure take up a lot of space"),
 * tinted by what it found -- green adds up, blue still to bill, amber something's off -- so it stands out. The figures
 * are server/contractBreakdown.js's; the top bar's and Job Cost tiles' drop-down (ContractBreakdownSummary) says the same.
 * ────────────────────────────────────────────────────────────────── */

const Icon = ({ tone }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {tone === 'warning'
      ? <><path d="M12 9v4" /><path d="M10.4 3.9 2.6 17.5A1.8 1.8 0 0 0 4.2 20h15.6a1.8 1.8 0 0 0 1.6-2.5L13.6 3.9a1.8 1.8 0 0 0-3.2 0Z" /><path d="M12 17h.01" /></>
      : tone === 'success'
        ? <><circle cx="12" cy="12" r="9" /><path d="m8.5 12 2.5 2.5 4.5-5" /></>
        : <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>}
  </svg>
);

const ContractBreakdownCard = ({ projectId, refreshKey }) => {
  const b = useContractBreakdown(projectId, refreshKey);
  if (!b) return null;

  const over = b.status === 'over';
  const docs = (b.own && b.own.documents) || [];
  const reason = reasonOf(b);
  const finding = findingOf(b);
  const tone = toneColors[finding.tone];
  const note = summitSovNoteOf(b);
  const docsSub = docs.length
    ? `${docLabelOf(docs[0])}${docs.length > 1 ? ` + ${docs.length - 1} more` : ''}`
    : null;
  const docsTitle = docs.map((d) => `${docLabelOf(d)} · ${money(d.total)}`).join('\n');

  return (
    <div data-testid="contract-breakdown">
      <div style={{ ...sovStripStyle, background: tone.soft, border: `1.5px solid ${tone.ring}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flex: '0 0 auto', padding: '0 12px 0 8px', color: tone.fg }}>
          <Icon tone={finding.tone} />
          <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-1)', whiteSpace: 'nowrap' }}>Contract check</span>
        </div>
        <SovStripDivider />
        <SovStripCell label="Contract on file" value={money(b.contract)} />
        <SovStripOp>=</SovStripOp>
        <SovStripCell label="SOV on the pay apps" value={money(b.sov.total)} sub={sovSubOf(b)} />
        <SovStripOp>+</SovStripOp>
        <SovStripCell label="Freeform invoices" value={money(b.own.total)} valueColor={b.own.total ? undefined : 'var(--text-4)'}
          sub={docsSub} subTitle={docsTitle} />
        <SovStripOp>{over ? '−' : '+'}</SovStripOp>
        <SovStripCell label={over ? 'Over the contract' : 'Not billed yet'}
          value={money(Math.abs(b.not_billed) > 1 ? b.not_billed : 0)}
          valueColor={b.status === 'never_billed' || over ? 'var(--warning)' : Math.abs(b.not_billed) > 1 ? undefined : 'var(--text-4)'}
          sub={reason || null} />
        <span style={{
          flex: '0 0 auto', margin: '0 8px 0 6px', padding: '3px 10px', borderRadius: 6, fontSize: 12.5, fontWeight: 600,
          color: tone.fg, background: 'var(--bg-surface)', border: `1px solid ${tone.ring}`, whiteSpace: 'nowrap',
        }}>
          {finding.text}
        </span>
      </div>
      {note && <div style={{ margin: '6px 0 0 4px', fontSize: 12.5, fontWeight: 500, color: 'var(--warning)' }}>{note}</div>}
    </div>
  );
};

export default ContractBreakdownCard;
